import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { getToken, isLoaded } = useAuth();
  const [isUserEnsured, setIsUserEnsured] = useState(false);

  useEffect(() => {
    async function ensureUserExists() {
      if (!isLoaded) return;

      try {
        const token = await getToken();
        if (!token) {
          console.log('[ProtectedRoute] No token, skipping user creation');
          return;
        }

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
        const response = await fetch(`${apiUrl}/api/auth/ensure-user`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error('[ProtectedRoute] Failed to ensure user:', response.status);
        } else {
          const data = await response.json();
          console.log('[ProtectedRoute] User ensured:', data.created ? 'created' : 'exists');
        }
      } catch (error) {
        console.error('[ProtectedRoute] Error ensuring user:', error);
      } finally {
        setIsUserEnsured(true);
      }
    }

    ensureUserExists();
  }, [getToken, isLoaded]);

  return (
    <>
      <SignedIn>
        {isUserEnsured ? (
          children
        ) : (
          <div className="min-h-screen bg-brand-background flex items-center justify-center">
            <Loader2 className="w-12 h-12 animate-spin text-brand-primary" />
          </div>
        )}
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}
